import { chordPitchClasses, parseSymbol } from '../theory/chords';
import { keysWithAccidentals, type KeySignature } from '../theory/keys';
import { midiOf, pitchClassOfMidi, spell, type Spelled } from '../theory/notes';
import { getShape, shapesFor, ALL_SHAPES } from './shapes';
import {
  defaultTuning,
  defaultVariant,
  type ChordShape,
  type HoleId,
  type HoleState,
  type InstrumentProfile,
  type Tuning,
} from './types';

/**
 * Instrument reasoning: where a note lives on the neck, which fingering produces
 * it on an ocarina, and how to fit music an instrument cannot otherwise play.
 */

export interface FretPosition {
  /** 1 is the lowest-numbered string in the tuning. */
  string: number;
  fret: number;
  midi: number;
}

/** Every place a pitch can be played, low frets first. */
export function positionsFor(
  midi: number,
  tuning: Tuning,
  options: { maxFret?: number } = {},
): FretPosition[] {
  const maxFret = options.maxFret ?? tuning.frets;
  const out: FretPosition[] = [];

  tuning.midis.forEach((open, index) => {
    const fret = midi - open;
    if (fret >= 0 && fret <= maxFret) out.push({ string: index + 1, fret, midi });
  });

  return out.sort((a, b) => a.fret - b.fret || a.string - b.string);
}

/** The pitch a string and fret produce. */
export function midiAt(tuning: Tuning, string: number, fret: number): number {
  const open = tuning.midis[string - 1];
  if (open === undefined) throw new Error(`String ${string} does not exist in ${tuning.id}`);
  return open + fret;
}

/** Every pitch a tuning can produce, for range checks. */
export function tuningRange(tuning: Tuning): { low: number; high: number } {
  const lows = tuning.midis;
  return {
    low: Math.min(...lows),
    high: Math.max(...lows.map((m) => m + tuning.frets)),
  };
}

export function isPlayable(midi: number, tuning: Tuning, maxFret?: number): boolean {
  return positionsFor(midi, tuning, maxFret === undefined ? {} : { maxFret }).length > 0;
}

/* --------------------------------------------------------- position solver */

export interface FingeringPlan {
  positions: FretPosition[];
  /** Total movement cost; lower is easier to play. */
  cost: number;
}

/**
 * Chooses where to play a melody, minimizing hand movement.
 *
 * A Viterbi pass over the candidate positions for each note. At low difficulty
 * open strings are rewarded, because that is what a beginner reaches for.
 */
export function solveFingering(
  midis: readonly number[],
  tuning: Tuning,
  options: { maxFret?: number; preferOpen?: boolean } = {},
): FingeringPlan | null {
  if (midis.length === 0) return { positions: [], cost: 0 };

  const maxFret = options.maxFret ?? tuning.frets;
  const preferOpen = options.preferOpen ?? false;

  const candidates = midis.map((m) => positionsFor(m, tuning, { maxFret }));
  if (candidates.some((c) => c.length === 0)) return null;

  const openBonus = (p: FretPosition): number => (preferOpen && p.fret === 0 ? -0.3 : 0);

  // best[i][j] = cheapest path ending at candidate j of note i.
  let previousCosts = (candidates[0] as FretPosition[]).map((p) => p.fret * 0.05 + openBonus(p));
  let paths: number[][] = (candidates[0] as FretPosition[]).map((_, j) => [j]);

  for (let i = 1; i < candidates.length; i += 1) {
    const here = candidates[i] as FretPosition[];
    const previous = candidates[i - 1] as FretPosition[];
    const costs: number[] = [];
    const nextPaths: number[][] = [];

    here.forEach((position, j) => {
      let bestCost = Infinity;
      let bestFrom = 0;

      previous.forEach((from, k) => {
        const fretMove = Math.abs(position.fret - from.fret) * 1.0;
        const stringMove = Math.abs(position.string - from.string) * 0.6;
        // Staying inside a four-fret hand position is much easier.
        const stretch = Math.abs(position.fret - from.fret) > 4 ? 2.0 : 0;
        const total = (previousCosts[k] as number) + fretMove + stringMove + stretch;
        if (total < bestCost) {
          bestCost = total;
          bestFrom = k;
        }
      });

      costs.push(bestCost + openBonus(position));
      nextPaths.push([...(paths[bestFrom] as number[]), j]);
    });

    previousCosts = costs;
    paths = nextPaths;
  }

  let best = 0;
  for (let i = 1; i < previousCosts.length; i += 1) {
    if ((previousCosts[i] as number) < (previousCosts[best] as number)) best = i;
  }

  const chosen = (paths[best] as number[]).map(
    (candidateIndex, noteIndex) =>
      (candidates[noteIndex] as FretPosition[])[candidateIndex] as FretPosition,
  );

  return { positions: chosen, cost: previousCosts[best] as number };
}

/* ---------------------------------------------------------------- shapes */

/** The sounding pitches a shape produces, low to high. */
export function shapePitches(shape: ChordShape, tuning: Tuning): number[] {
  const out: number[] = [];
  shape.frets.forEach((fret, index) => {
    if (fret === null) return;
    const open = tuning.midis[index];
    if (open === undefined) return;
    out.push(open + fret);
  });
  return out.sort((a, b) => a - b);
}

export function shapePitchClasses(shape: ChordShape, tuning: Tuning): number[] {
  return [...new Set(shapePitches(shape, tuning).map(pitchClassOfMidi))].sort((a, b) => a - b);
}

/**
 * Checks a shape really produces the chord it claims.
 *
 * Returns the problems found, so the caller can report all of them at once.
 * Some shapes legitimately omit a note (a ukulele has four strings and a
 * thirteenth chord has six notes), so omissions are allowed but wrong notes
 * are not.
 */
export function verifyShape(shape: ChordShape, tuning: Tuning): string[] {
  const problems: string[] = [];

  let expected: number[];
  try {
    const parsed = parseSymbol(shape.symbol);
    expected = chordPitchClasses(parsed.root, parsed.chordId);
  } catch (err) {
    return [`${shape.id}: cannot parse symbol "${shape.symbol}": ${String(err)}`];
  }

  const actual = shapePitchClasses(shape, tuning);
  const expectedSet = new Set(expected);

  const wrong = actual.filter((pc) => !expectedSet.has(pc));
  if (wrong.length > 0) {
    problems.push(
      `${shape.id} (${shape.symbol}) sounds pitch classes ${actual.join(',')} but the chord is ${expected.join(',')}; extra: ${wrong.join(',')}`,
    );
  }

  // The root and the third (or the chord's defining note) should be present.
  const root = expected[0];
  if (
    root !== undefined &&
    !actual.includes(root) &&
    shape.frets.filter((f) => f !== null).length >= 3
  ) {
    problems.push(`${shape.id} (${shape.symbol}) omits its root`);
  }

  if (shape.fingers !== undefined && shape.fingers.length !== shape.frets.length) {
    problems.push(
      `${shape.id} has ${shape.fingers.length} fingers for ${shape.frets.length} strings`,
    );
  }

  return problems;
}

/** Verifies every shape in the library. Used by tests and content validation. */
export function verifyAllShapes(ukuleleTuning: Tuning, guitarTuning: Tuning): string[] {
  return ALL_SHAPES.flatMap((shape) =>
    verifyShape(shape, shape.instrument === 'ukulele' ? ukuleleTuning : guitarTuning),
  );
}

/** Best shape for a chord on an instrument, respecting a difficulty tier. */
export function shapeFor(
  symbol: string,
  instrument: 'ukulele' | 'guitar',
  options: { maxTier?: number } = {},
): ChordShape | null {
  return shapesFor(symbol, instrument, options)[0] ?? null;
}

/**
 * Transposes a movable shape up the neck. Open strings become barred, so the
 * result is only valid for shapes marked movable.
 */
export function transposeShape(shape: ChordShape, semitones: number): ChordShape | null {
  if (shape.movable !== true || semitones === 0) return semitones === 0 ? shape : null;

  const frets = shape.frets.map((f) => (f === null ? null : f + semitones));
  if (frets.some((f) => f !== null && (f < 0 || f > 20))) return null;

  return {
    ...shape,
    id: `${shape.id}+${semitones}`,
    frets,
    ...(shape.barre !== undefined
      ? { barre: { ...shape.barre, fret: shape.barre.fret + semitones } }
      : {}),
  };
}

/* -------------------------------------------------------------- ocarina */

export interface FingeringLookup {
  fingering: Record<HoleId, HoleState>;
  verified: boolean;
  note?: string;
}

/** The fingering for a pitch on an ocarina variant, if the chart has one. */
export function fingeringFor(
  profile: InstrumentProfile,
  midi: number,
  variantId?: string,
): FingeringLookup | null {
  const variant =
    variantId === undefined
      ? defaultVariant(profile)
      : profile.variants?.find((v) => v.id === variantId);
  if (variant === undefined) return null;

  // The chart is written for an alto C; a transposing variant shifts the lookup.
  const chartMidi = midi - variant.transposeFromAC;
  const spelled = spell(chartMidi, { accidentals: 0 });
  const name = `${spelled.letter}${spelled.alter > 0 ? '#'.repeat(spelled.alter) : 'b'.repeat(-spelled.alter)}${spelled.octave}`;

  const found = variant.fingerings[name];
  if (found === undefined) return null;

  return {
    fingering: found.holes,
    verified: found.verified,
    ...(found.note !== undefined ? { note: found.note } : {}),
  };
}

/* ------------------------------------------------------------- auto-fit */

export interface AutoFitResult {
  /** Semitones to transpose by; 0 when only an octave shift was needed. */
  semitones: number;
  octaves: number;
  newKey?: KeySignature;
  reason: 'fits' | 'octave' | 'transposed' | 'impossible';
}

/**
 * Makes music playable on an instrument.
 *
 * Tries the original, then octave shifts, then transposition, preferring keys
 * with few accidentals and, on fretted instruments, keys with open chords.
 */
export function autoFit(
  midis: readonly number[],
  profile: InstrumentProfile,
  options: { key?: KeySignature } = {},
): AutoFitResult {
  if (midis.length === 0) return { semitones: 0, octaves: 0, reason: 'fits' };

  const { low, high } = profile.comfortable;
  const lowest = Math.min(...midis);
  const highest = Math.max(...midis);

  if (lowest >= low && highest <= high) return { semitones: 0, octaves: 0, reason: 'fits' };

  // Octave shifts keep the key, so try them first.
  const byOctave: { octaves: number; centerDistance: number }[] = [];
  for (const octaves of [-2, -1, 1, 2]) {
    const shift = octaves * 12;
    if (lowest + shift >= low && highest + shift <= high) {
      const center = (lowest + highest) / 2 + shift;
      byOctave.push({ octaves, centerDistance: Math.abs(center - (low + high) / 2) });
    }
  }
  if (byOctave.length > 0) {
    byOctave.sort((a, b) => a.centerDistance - b.centerDistance);
    return {
      semitones: 0,
      octaves: (byOctave[0] as { octaves: number }).octaves,
      reason: 'octave',
    };
  }

  // Otherwise transpose, favouring simple keys.
  const friendly = friendlyKeyPitchClasses(profile);
  const candidates: { semitones: number; rank: number }[] = [];

  for (let semitones = -11; semitones <= 11; semitones += 1) {
    for (const octaves of [-2, -1, 0, 1, 2]) {
      const shift = semitones + octaves * 12;
      if (lowest + shift < low || highest + shift > high) continue;

      const newTonicPc =
        options.key === undefined ? null : pitchClassOfMidi(midiOf(options.key.tonic) + semitones);

      const rank =
        Math.abs(semitones) * 0.1 + (newTonicPc !== null && friendly.has(newTonicPc) ? 0 : 1.5);

      candidates.push({ semitones: shift, rank });
    }
  }

  if (candidates.length === 0) return { semitones: 0, octaves: 0, reason: 'impossible' };

  candidates.sort((a, b) => a.rank - b.rank || Math.abs(a.semitones) - Math.abs(b.semitones));
  const best = candidates[0] as { semitones: number };

  const result: AutoFitResult = { semitones: best.semitones, octaves: 0, reason: 'transposed' };
  if (options.key !== undefined) {
    const target = keysWithAccidentals(0, 7, options.key.mode).find(
      (k) =>
        pitchClassOfMidi(midiOf(k.tonic)) ===
        pitchClassOfMidi(midiOf(options.key!.tonic) + best.semitones),
    );
    if (target !== undefined) result.newKey = target;
  }
  return result;
}

/** Keys a fretted instrument plays comfortably, as tonic pitch classes. */
function friendlyKeyPitchClasses(profile: InstrumentProfile): Set<number> {
  if (profile.id === 'ukulele') return new Set([0, 7, 2, 9, 5, 4]); // C G D A F E
  if (profile.id === 'guitar') return new Set([4, 9, 2, 7, 0]); // E A D G C
  if (profile.id === 'ocarina') return new Set([0, 5, 7, 2]); // C F G D
  return new Set([0, 7, 5, 2, 9]);
}

/* ------------------------------------------------------- transposing parts */

/** Written pitch for a transposing instrument, given the sounding pitch. */
export function writtenPitch(soundingMidi: number, profile: InstrumentProfile): number {
  return soundingMidi - profile.writtenOctaveOffset * 12;
}

export function soundingPitch(writtenMidi: number, profile: InstrumentProfile): number {
  return writtenMidi + profile.writtenOctaveOffset * 12;
}

/* ------------------------------------------------------------ description */

/** "C string, 4th fret" or "all holes closed but the right pinky". */
export function describePosition(profile: InstrumentProfile, midi: number): string | null {
  if (profile.family === 'fretted') {
    const tuning = defaultTuning(profile);
    if (tuning === undefined) return null;
    const position = positionsFor(midi, tuning)[0];
    if (position === undefined) return null;

    const openMidi = tuning.midis[position.string - 1] as number;
    const stringName = spell(openMidi, { accidentals: 0 }).letter;
    if (position.fret === 0) return `open ${stringName} string`;
    return `${stringName} string, fret ${position.fret}`;
  }

  if (profile.family === 'wind') {
    const lookup = fingeringFor(profile, midi);
    if (lookup === null) return null;
    const open = Object.entries(lookup.fingering)
      .filter(([, state]) => state === 'open')
      .map(([id]) => id);
    return open.length === 0 ? 'all holes closed' : `open: ${open.join(', ')}`;
  }

  return null;
}

/** The interval between two notes, described on the instrument. */
export function describeInterval(
  profile: InstrumentProfile,
  low: Spelled,
  high: Spelled,
): string | null {
  const from = describePosition(profile, midiOf(low));
  const to = describePosition(profile, midiOf(high));
  if (from === null || to === null) return null;
  return `${from} to ${to}`;
}

export { getShape, shapesFor };
